import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SettingsSidePanelDefaults, SettingsSidePanelOptions } from './settings-side-panel.options';
import { SettingsSidePanelService } from './settings-side-panel.service';

/**
 * New, standalone side-panel shell for settings screens (LUNA-26). Deliberately not a
 * variant of `SidePanelModal`/`cs-side-panel-v2` — no backdrop, no full-viewport-fixed host,
 * fixed header + scrollable body via flexbox instead of absolutely-positioned chrome, no
 * built-in footer/button bar (Save/Cancel are part of whatever content is projected in, so
 * their position follows the content instead of being pinned to the panel bottom).
 */
@Component({
  selector: 'cs-settings-side-panel',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './settings-side-panel.html',
  styleUrl: './settings-side-panel.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  host: {
    class: 'cs-settings-side-panel-host',
    role: 'dialog',
    tabindex: '-1',
    '[attr.aria-label]': 'config?.title || null',
  },
})
export class SettingsSidePanel implements OnInit, AfterViewInit, OnDestroy {
  config: SettingsSidePanelOptions;
  isShown = false;

  content?: unknown;
  isTemplate = false;
  isComponent = false;
  isString = false;
  isDom = false;
  isForeignTemplate = false;

  private foreignViewRef: any;
  private closeTimeout?: ReturnType<typeof setTimeout>;
  private unlistenDocumentClick?: () => void;
  // Stable reference so add/removeEventListener target the same function — Renderer2.listen
  // can't be used here (see ngOnInit's registration for why capture phase is required).
  private readonly documentClickHandler = (event: MouseEvent) => {
    if (!this._element.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  };

  @ViewChild('domContainer') domContainer!: ElementRef;

  constructor(
    options: SettingsSidePanelOptions,
    private panelService: SettingsSidePanelService,
    private ngZone: NgZone,
    protected _element: ElementRef,
    private _renderer: Renderer2,
    private destroyRef: DestroyRef,
  ) {
    this.config = Object.assign({}, SettingsSidePanelDefaults, options);
    this.content = this.config.content;
  }

  ngOnInit(): void {
    if (this.config.size && this.config.size !== 'standard') {
      this._renderer.addClass(this._element.nativeElement, this.config.size);
    }

    this._renderer.setStyle(this._element.nativeElement, 'display', 'flex');

    setTimeout(() => {
      this.isShown = true;
      this._renderer.addClass(this._element.nativeElement, 'show');
      // Deferred past the opening click's own event so that click doesn't immediately close the panel.
      // Capture phase, not Renderer2.listen('document', ...) (bubble phase) — the v4 navbar's
      // Notifications/Profile icons are plain Bootstrap .dropdown-toggle elements whose own click
      // handler calls stopPropagation(), which would otherwise stop this click from ever bubbling
      // up to document at all, meaning close() would never run for those two icons specifically
      // (confirmed live: clicking them left the panel's `.show` class and z-index untouched).
      // Capture fires top-down, before the target's own handler runs, so it can't be suppressed
      // that way — Renderer2.listen has no capture-phase option, hence the raw DOM API here.
      document.addEventListener('click', this.documentClickHandler, true);
      this.unlistenDocumentClick = () => document.removeEventListener('click', this.documentClickHandler, true);
    }, this.config.animated === false ? 0 : 20);

    if (this.content != null) {
      if (typeof this.content === 'string') {
        this.isString = true;
      } else if ((this.content as { nodeType?: unknown }).nodeType) {
        // Raw DOM node
        this.isDom = true;
      } else if (this.content instanceof TemplateRef) {
        // Local TemplateRef (same bundle)
        this.isTemplate = true;
      } else if (
        (this.content as any).elementRef &&
        ((this.content as any)._declarationLView || (this.content as any)._declarationTContainer)
      ) {
        // Foreign TemplateRef (from another bundle, e.g. a settings MFE) — ngTemplateOutlet
        // can't render it directly (crashes with a firstUpdatePass error across bundles), so
        // it's rendered manually in ngAfterViewInit via its own createEmbeddedView.
        this.isForeignTemplate = true;
      } else if (typeof this.content === 'function') {
        this.isComponent = true;
      }
    }
  }

  ngAfterViewInit(): void {
    if (!this.domContainer) {
      return;
    }

    if (this.isDom && this.content) {
      this._renderer.appendChild(this.domContainer.nativeElement, this.content);
      return;
    }

    if (this.isForeignTemplate && this.content && typeof (this.content as any).createEmbeddedView === 'function') {
      this.ngZone.run(() => {
        try {
          const view = (this.content as any).createEmbeddedView(this.config.initialState || {});
          this.foreignViewRef = view;
          view.detectChanges();
          view.rootNodes.forEach((node: any) => {
            this._renderer.appendChild(this.domContainer.nativeElement, node);
          });

          this.ngZone.onMicrotaskEmpty.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            if (this.foreignViewRef && !this.foreignViewRef.destroyed) {
              this.foreignViewRef.detectChanges();
            }
          });

          // Second pass after a macrotask — a microtask alone is sometimes too fast for
          // cross-bundle child components to have hydrated.
          setTimeout(() => {
            if (this.foreignViewRef && !this.foreignViewRef.destroyed) {
              this.foreignViewRef.detectChanges();
            }
          }, 0);
        } catch (e) {
          console.error('SettingsSidePanel: failed to render foreign template', e);
        }
      });
    }
  }

  close(): void {
    if (this.closeTimeout) {
      return;
    }
    this.unlistenDocumentClick?.();
    this.unlistenDocumentClick = undefined;
    this._renderer.removeClass(this._element.nativeElement, 'show');
    // Keep in sync with $settings-panel-transition-duration in settings-side-panel.scss.
    const delay = this.config.animated === false ? 0 : 550;
    this.closeTimeout = setTimeout(() => {
      this.isShown = false;
      this.config.onClose?.();
      this.panelService.remove(this.config.id);
    }, delay);
  }

  ngOnDestroy(): void {
    this.unlistenDocumentClick?.();
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
    }
    if (this.foreignViewRef && !this.foreignViewRef.destroyed) {
      try {
        this.foreignViewRef.destroy();
      } catch {
        // already destroyed
      }
    }
  }
}
